import React, { useState, useEffect } from "react";
import { Select, Button, DatePicker } from "antd";
import { getProjectsData, getWorkersData, getVendorData, getUser, getManagers } from "../../services/service";
import { DropDown } from "../../assets/icons";
import { getFilterConfig } from "../../config/filterConfig";

const { Option } = Select;
const { RangePicker } = DatePicker;

const planOptions = [
  { value: 'all', label: 'All Plans' },
  { value: 'basic', label: 'Basic' },
  { value: 'premium', label: 'Premium' },
  { value: 'ultimate', label: 'Ultimate' },
];

const FilterForm = ({ handleSearchChange, setDropdownVisible, showWorkerFilter, initialProjectId, showProjectFilter = true, moduleType = 'default' }) => {
  const [projects, setProjects] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [managers, setManagers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDirectWorker, setSelectedDirectWorker] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPlan, setSelectedPlan] = useState('all');
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [workersLoading, setWorkersLoading] = useState(false);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [managersLoading, setManagersLoading] = useState(false);


  useEffect(() => {
    const fetchProjects = async () => {
      setProjectsLoading(true);
      try {
        const response = await getProjectsData("", 1, 100, "");
        if (response?.success) {
          setProjects(response?.data || []);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setProjectsLoading(false);
      }
    };
    fetchProjects();

    const fetchVendors = async () => {
      setVendorsLoading(true);
      try {
        const response = await getVendorData("", 1, 100, "", "");
        if (response?.success) {
          setVendors(response?.data || []);
        }
      } catch (error) {
        console.error("Error fetching vendors:", error);
      } finally {
        setVendorsLoading(false);
      }
    };
    fetchVendors();
  }, []);

  useEffect(() => {
    if (selectedUserType === 'worker' || moduleType === 'dashboard-workers') {
      const fetchWorkers = async () => {
        setWorkersLoading(true);
        try {
          const response = await getWorkersData("", 100, 1, "");
          if (response?.success) {
            setWorkers(response?.data || []);
          }
        } catch (error) {
          console.error("Error fetching workers:", error);
        } finally {
          setWorkersLoading(false);
        }
      };
      fetchWorkers();
    }
  }, [selectedUserType, moduleType]);

  useEffect(() => {
    if (initialProjectId) {
      setSelectedProject(initialProjectId);
    }
  }, [initialProjectId]);

  const handleApply = () => {
    handleSearchChange({
      project_id: selectedProject,
      worker_id: selectedUserType === 'worker' ? selectedUser : (moduleType === 'dashboard-workers' ? selectedDirectWorker : ''),
      startDate: dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : "",
      endDate: dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : "",
      vendor_id: selectedVendor,
      created_by_role: selectedUserType,
      user_type: selectedUserType,
      user_id: selectedUser,
      created_by_user: selectedUser || selectedDirectWorker || '',
      status: selectedStatus,
      plan: selectedPlan,
    });
    setDropdownVisible(false);
  };

  const handleClear = () => {
    setSelectedProject(null);
    setSelectedVendor(null);
    setSelectedUserType(null);
    setSelectedUser(null);
    setSelectedDirectWorker(null);
    setDateRange([]);
    setSelectedStatus('all');
    setSelectedPlan('all');
    handleSearchChange({
      project_id: '',
      worker_id: '',
      startDate: '',
      endDate: '',
      vendor_id: '',
      created_by_role: '',
      user_type: '',
      user_id: '',
      created_by_user: '',
      status: 'all',
      plan: 'all'
    });
    setDropdownVisible(false);
  };

  const handleProjectChange = (value) => {
    setSelectedProject(value);
    // Clear worker selection when project changes
    setSelectedUser(null);
  };

  // Get filter configuration for the current module type
  const filterConfig = getFilterConfig(moduleType, showProjectFilter);

  return (
    <div className="p-4 -mt-2 -ml-3 w-[368px] rounded-[8px] bg-white filter-shadow">
      <div className="flex flex-col gap-3 font-medium text-[14px] text-[#4B5563]">
        {filterConfig.showDateRange && (
          <div className="flex flex-col gap-2">
            <p>Date range</p>
            <RangePicker
              className="w-full h-[44px] rounded-[4px] border border-[#E9E9E9]"
              value={dateRange}
              onChange={(val) => setDateRange(val || [])}
              allowEmpty={[true, true]}
            />
          </div>
        )}

        {filterConfig.showProject && showProjectFilter && (
          <div className="flex flex-col gap-2">
            <p>Project</p>
            <Select
              placeholder="Choose project"
              onChange={handleProjectChange}
              value={selectedProject}
              className="w-full h-[44px] rounded-[4px] border border-[#E9E9E9] focus:outline-none"
              suffixIcon={DropDown}
              allowClear
              loading={projectsLoading}
            >
              <Option value="">All Projects</Option>
              {projects.map((project) => (
                <Option key={project.id} value={project.id}>
                  {project.title}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {filterConfig.showStatus && (
          <div className="flex flex-col gap-2">
            <p>Status</p>
            <Select
              placeholder="Select status"
              onChange={(value) => setSelectedStatus(value)}
              value={selectedStatus}
              className="w-full h-[44px] rounded-[4px] border border-[#E9E9E9] focus:outline-none"
              suffixIcon={DropDown}
            >
              <Option value="all">All</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              {moduleType?.includes('project') && (
                <Option value="completed">Completed</Option>
              )}
            </Select>
          </div>
        )}

        {filterConfig.showPlan && (
          <div className="flex flex-col gap-2">
            <p>Plan</p>
            <Select
              placeholder="Select plan"
              onChange={(value) => setSelectedPlan(value)}
              value={selectedPlan}
              className="w-full h-[44px] rounded-[4px] border border-[#E9E9E9] focus:outline-none"
              suffixIcon={DropDown}
            >
              {planOptions.map((plan) => (
                <Option key={plan.value} value={plan.value}>
                  {plan.label}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {filterConfig.showVendor && (
          <div className="flex flex-col gap-2">
            <p>Vendor</p>
            <Select
              placeholder="Choose vendor"
              onChange={(value) => setSelectedVendor(value)}
              value={selectedVendor}
              className="w-full h-[44px] rounded-[4px] border border-[#E9E9E9] focus:outline-none"
              suffixIcon={DropDown}
              allowClear
              loading={vendorsLoading}
            >
              <Option value="">All Vendors</Option>
              {vendors.map((vendor) => (
                <Option key={vendor.id} value={vendor.id}>
                  {vendor.title}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {filterConfig.showCreatedBy && (
          <div className="flex flex-col gap-2">
            <p>Created by</p>
            <Select
              placeholder="Select created by"
              onChange={(value) => {
                setSelectedUserType(value);
                setSelectedUser(null);
                if (value === 'manager') {
                  setManagersLoading(true);
                  getManagers(1, 100)
                    .then((response) => {
                      if (response?.success) {
                        setManagers(response?.data || response?.managers || []);
                      }
                    })
                    .catch((e) => console.error('Error fetching managers:', e))
                    .finally(() => setManagersLoading(false));
                }
              }}
              value={selectedUserType}
              className="w-full h-[44px] rounded-[4px] border border-[#E9E9E9] focus:outline-none"
              suffixIcon={DropDown}
              allowClear
            >
              <Option value="">All Types</Option>
              <Option value="worker">Worker</Option>
              <Option value="manager">Manager</Option>
            </Select>
          </div>
        )}

        {filterConfig.showWorker && selectedUserType && (
          <div className="flex flex-col gap-2">
            <p>Worker</p>
            <Select
              placeholder={`Choose ${selectedUserType}`}
              onChange={(value) => setSelectedUser(value)}
              value={selectedUser}
              className="w-full h-[44px] rounded-[4px] border border-[#E9E9E9] focus:outline-none"
              suffixIcon={DropDown}
              allowClear
              loading={selectedUserType === 'manager' ? managersLoading : workersLoading}
            >
              <Option value="">All {selectedUserType === 'manager' ? 'Managers' : 'Workers'}</Option>
              {(selectedUserType === 'manager' ? managers : workers).map((user) => (
                <Option key={user.id} value={user.id}>
                  {user.username || user.name || user.title}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {filterConfig.showDirectWorker && (
          <div className="flex flex-col gap-2">
            <p>Worker</p>
            <Select
              placeholder="Choose worker"
              onChange={(value) => setSelectedDirectWorker(value)}
              value={selectedDirectWorker}
              className="w-full h-[44px] rounded-[4px] border border-[#E9E9E9] focus:outline-none"
              suffixIcon={DropDown}
              allowClear
              loading={workersLoading}
            >
              <Option value="">All Workers</Option>
              {workers.map((worker) => (
                <Option key={worker.id} value={worker.id}>
                  {worker.username || worker.name || worker.title}
                </Option>
              ))}
            </Select>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            onClick={handleClear}
            className="w-full h-[44px] font-medium text-[13px] bg-[#1E3A8A] border-[#1E3A8A] text-white hover:!bg-[#1a3175] hover:!border-[#1a3175] hover:!text-white transition-all duration-300"
          >
            Clear Filter
          </Button>
          <Button
            type="submit"
            onClick={handleApply}
            className="bg-[#F97316] border border-[#F97316] w-full h-[44px] font-medium text-[13px] text-white"
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterForm;
